/**
 * BossArrival (S195 / BL-282, audit beat #8; re-routed by D-331, 2026-07-31).
 *
 * This used to slam the boss's localized name across the middle of the play
 * grid — exactly the in-grid banner the owner flagged from a screenshot
 * ("BOSS-DURCHBRUCH" filling the board). The owner's two-tier decision moved
 * every STORY/NARRATION/PRAISE/STATUS message off the grid onto the
 * commentary TV (Vera/Fenrir); a boss's identity reveal is squarely that
 * category — it names a threat, it doesn't demand an immediate player
 * reaction the way "CORE BREAKER DETECTED" does.
 *
 * This module keeps its one genuinely load-bearing job: the real-boss guard
 * (finalBoss | spawnAdds only — the WaveManager.isBoss vocabulary) and the
 * i18n name recovery (`Enemy.type` carries the config NAME, not the id; the
 * id is matched back for `localizedName`), because BossArrival is the only
 * place that already holds the enemies config table. It no longer touches
 * the DOM at all — the resolved `title`/`name` pair is handed to the caller
 * (`onNameSlam`, wired to `CommsDirector.announceBossArrival` via the HUD in
 * main.ts) instead of building a lower-third strip. `announce()` still fires
 * here so the a11y mirror is on the SAME choke point it always was,
 * independent of whatever the TV ends up doing with the visual beat.
 */
import type { EventBus } from "../core/EventBus.ts";
import type { EnemyConfig } from "../entities/types.ts";
import { t, localizedName } from "../utils/i18n.ts";
import { announce } from "./a11y.ts";

/** Two Core Breakers erupting back-to-back re-use the last announcement
 *  instead of double-announcing a second slam within the same beat. */
const MIN_INTERVAL_MS = 1500;

/** The real-boss guard — the same vocabulary as WaveManager.isBoss /
 *  AudioEngine's isRealBoss (finalBoss | spawnAdds; BL-163 retired HP heuristics). */
function isRealBoss(boss: { special?: { type?: string } } | undefined): boolean {
  const t_ = boss?.special?.type;
  return t_ === "finalBoss" || t_ === "spawnAdds";
}

export class BossArrival {
  private unsubs: Array<() => void> = [];
  private enemies: Record<string, EnemyConfig> | null = null;
  private lastShownAt = 0;
  private onNameSlam: ((title: string, name: string) => void) | undefined;

  /** @param enemies the loaded enemy config table (`data.enemies`) — the
   *  name→id mapping seam EnemyReveal already uses (`Enemy.type` carries the
   *  config NAME, not the id, so the id is recovered by name-match).
   *  @param onNameSlam D-331: routes the resolved boss identity to the TV
   *  (`CommsDirector.announceBossArrival`) instead of an in-grid strip. */
  attach(
    bus: EventBus,
    enemies: Record<string, EnemyConfig>,
    onNameSlam?: (title: string, name: string) => void,
  ): void {
    this.detach();
    this.enemies = enemies;
    this.onNameSlam = onNameSlam;
    this.unsubs.push(
      bus.on("boss.spawned", (d: unknown) => {
        const ev = d as { boss?: { type?: string; special?: { type?: string } } };
        if (!isRealBoss(ev.boss)) return;
        this.announce(ev.boss?.type ?? "");
      }),
    );
  }

  detach(): void {
    for (const off of this.unsubs) off();
    this.unsubs = [];
    this.lastShownAt = 0;
  }

  private announce(rawName: string): void {
    const now = Date.now();
    if (now - this.lastShownAt < MIN_INTERVAL_MS) return;
    this.lastShownAt = now;

    // Recover the config id for i18n (EnemyReveal's seam); fall back to the
    // raw config name so a missing entry never renders a key.
    const id = Object.entries(this.enemies ?? {}).find(
      ([, c]) => c.name === rawName,
    )?.[0];
    const name = id ? localizedName("enemy", id, rawName) : rawName;
    const title = t("boss.arrival.title");

    announce(`${title}: ${name}`);
    this.onNameSlam?.(title, name);
  }
}
